import type React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, Box, Card, Heading, Icon, Image, Menu, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconCreditScoreFilled } from '@zillow/constellation-icons';
import ArrayCreditOverviewMini from './array/ArrayCreditOverviewMini';
import faqs from '../../../constants/FaqConstants';
import creditOverviewSvg from '../../../../../../assets/creditOverview.svg';
import ArrayAccountEnroll from './array/ArrayAccountEnroll';
import { CREDIT_REPORTING_FAQ_URL } from '../../../constants/rentReportingConstants';
import { CreditTrackingOptOutModal } from './CreditTrackingOptOutModal';
import type { RootState } from '../../../../../store/types';
import type { CreditTrackingInterface } from '../../../models/Types';
import { requestUnenrollRenterInCreditTracking } from '../../../actions/requests';
import { CREDIT_TRACKING_UNENROLL_SUCCESS } from '@renter-hub/constants/notifications';
import { CREDIT_TRACKING_UNENROLL_FAILURE } from '@renter-hub/constants/notifications';
import { showPageNotification } from '@common/actions/NotificationActions';
import { updateCreditTrackingEnrollmentStatus } from '@renter-hub/actions/onboarding';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';

const CreditTrackingCard: React.FC = () => {
  const dispatch = useDispatch();
  const { arrayUserToken = null } = useSelector(
    (state: RootState) =>
      (state.payments.renter.dashboard.paymentReporting?.creditTracking as CreditTrackingInterface) ?? {},
  );

  const {
    firstName = '',
    lastName = '',
    email = '',
  } = useSelector((state: RootState) => state.payments.renter.dashboard.renterProfile);

  const {
    homeAddress: streetAddress = '',
    city = '',
    state = '',
    zipCode = '',
  } = useSelector((state: RootState) => state.payments.renter.dashboard.rentTerms.addressInfo);

  const { user } = useSelector((state: RootState) => state);

  const creditTrackingFaqs = faqs.PAYMENT_SETTINGS_V2.CREDIT_TRACKING.body();
  const [trackingOptOutModalOpen, setTrackingOptOutModalOpen] = useState(false);

  const handleUnenrollRenterFromCreditTracking = async () => {
    try {
      RachelCSEvents.emitOptOutCreditTrackingEvent();
      const response = await dispatch(requestUnenrollRenterInCreditTracking());
      checkRequestActionSuccess(response);
      dispatch(showPageNotification(CREDIT_TRACKING_UNENROLL_SUCCESS));
      await dispatch(updateCreditTrackingEnrollmentStatus('OPTED_OUT'));
      setTrackingOptOutModalOpen(false);
    } catch (err) {
      dispatch(showPageNotification(CREDIT_TRACKING_UNENROLL_FAILURE));
      setTrackingOptOutModalOpen(false);
    }
  };

  const handleExpandedChange = (value: string | Array<string>) => {
    if (value) {
      RachelCSEvents.emitFaqCreditTrackingEvent(value as string);
    }
  };

  const unenrolledContent = (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'token(spacing.xs)',
        padding: 'token(spacing.sm) !important',
      }}
    >
      <Box
        css={{
          display: 'flex',
          justifyContent: 'flex-start',
          '@media (min-width: 768px)': {
            justifyContent: 'center',
          },
        }}
      >
        <Image
          alt="credit-overview"
          css={{ height: '108px', marginBottom: '4px' }}
          src={creditOverviewSvg as unknown as string}
        />
      </Box>
      <Box
        css={{
          textAlign: 'left',
          maxWidth: '570px',
          margin: '0',
          '@media (min-width: 768px)': {
            textAlign: 'center',
            margin: '0 auto',
          },
        }}
      >
        <Paragraph textStyle="body-bold">Make the most of rent reporting</Paragraph>
        <Paragraph>
          Track your credit score directly on your payments dashboard while we report your payments. Zillow won&apos;t
          store any of your credit data.
        </Paragraph>
      </Box>
      <ArrayAccountEnroll
        firstName={firstName}
        lastName={lastName}
        streetAddress={streetAddress}
        city={city}
        state={state}
        zipCode={zipCode}
        email={email}
      />
    </Box>
  );

  const enrolledContent = (
    <>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'row',
          '@media (max-width: 768px)': {
            flexDirection: 'column',
          },
          gap: 'token(spacing.xs)',
          padding: '0 token(spacing.sm) token(spacing.sm) token(spacing.sm) !important',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        <Box css={{ flexBasis: '50%' }}>
          <ArrayCreditOverviewMini userToken={arrayUserToken} />
        </Box>
        <Box css={{ flexBasis: '50%' }}>
          <Box
            css={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'token(spacing.xs)',
            }}
          >
            <Heading level={6}>Frequently asked questions</Heading>
            <TextButton asChild tone="neutral" textStyle="body" css={{ textDecoration: 'underline' }}>
              <a href={CREDIT_REPORTING_FAQ_URL} target="_blank" rel="noreferrer">
                View all
              </a>
            </TextButton>
          </Box>
          <Accordion.Root type="single" onExpandedChange={handleExpandedChange}>
            {creditTrackingFaqs.map((faq, index) => (
              <Accordion.Item value={faq.key ?? `accordion-${index}`} key={faq.key ?? `accordion-${index}`}>
                <Accordion.Header css={{ padding: '12px 16px !important' }}>
                  <Paragraph>{faq.question}</Paragraph>
                </Accordion.Header>
                <Accordion.Panel css={{ padding: '12px 16px !important' }}>
                  <Paragraph>{faq.answer}</Paragraph>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      <Card
        elevated={false}
        header={
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '100%',
              padding: 'token(spacing.xs) token(spacing.xs) token(spacing.xs) token(spacing.sm) !important',
            }}
          >
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Icon render={<IconCreditScoreFilled />} css={{ marginRight: 'token(spacing.xs)' }} />
              <Heading level={6}>Credit tracking</Heading>
            </Box>
            {arrayUserToken && (
              <Menu
                placement="bottom-end"
                content={
                  <Box css={{ zIndex: '1000 !important' }}>
                    <Menu.Item>
                      <Menu.ItemLabel onClick={() => setTrackingOptOutModalOpen(true)}>
                        Opt out of credit tracking
                      </Menu.ItemLabel>
                    </Menu.Item>
                  </Box>
                }
              />
            )}
          </Box>
        }
      >
        {arrayUserToken ? enrolledContent : unenrolledContent}
      </Card>
      <CreditTrackingOptOutModal
        open={trackingOptOutModalOpen}
        onClose={() => setTrackingOptOutModalOpen(false)}
        onOptOut={handleUnenrollRenterFromCreditTracking}
      />
    </>
  );
};

export default CreditTrackingCard;
